/**
 * Multi-Calendar Integration Module
 * Supports ICS (private links) and CalDAV (Nextcloud, etc.)
 *
 * Calendar config is stored in User CRDT for cross-device sync.
 * Falls back to localStorage for unauthenticated users or migration.
 */

import * as UserCRDT from "../../../engine/sync/user-crdt-bridge.js";
import {
  showModal as showModalGeneric,
  closeModal as closeModalGeneric,
} from "../ui/modals.js";
import { t, formatDate } from "../../../shared/i18n/index.js";
import { escapeHtml } from "../../../engine/core/validation.js";

// ============================================
// Configuration & Constants
// ============================================

const NC_STORAGE_KEY = "orbital-calendars-config";
const NC_CALENDAR_COLORS = [
  "#00b4a0",
  "#e91e8c",
  "#4A90E2",
  "#FFB142",
  "#4ECB71",
  "#9b59b6",
  "#e74c3c",
  "#3498db",
  "#f39c12",
  "#1abc9c",
];

// Proxy configuration
const LOCAL_PROXY_ENDPOINT = "/proxy?url=";
let useLocalProxy = false;
let localProxyAvailable = false;

// Modal state
let ncModalCalendars = [];

// Dependency: escapeHtmlSafe function (injected)
let _escapeHtml = (str) => str;

/**
 * Initialize calendar module with dependencies
 * @param {Object} deps - Dependencies
 * @param {Function} deps.escapeHtml - HTML escape function
 */
export function initCalendar(deps = {}) {
  if (deps.escapeHtml) _escapeHtml = deps.escapeHtml;
}

// ============================================
// Proxy Detection
// ============================================

/**
 * Check if local proxy server is available
 * @returns {Promise<boolean>}
 */
export async function checkLocalProxy() {
  // Only check if served from localhost
  if (!window.location.hostname.match(/^(localhost|127\.0\.0\.1)$/)) {
    console.log("Not running from localhost, local proxy not available");
    return false;
  }

  try {
    const response = await fetch("/health", { method: "GET" });
    if (response.ok) {
      const data = await response.json();
      if (data.status === "ok") {
        console.log("Local proxy server detected and healthy");
        localProxyAvailable = true;
        return true;
      }
    }
  } catch (e) {
    console.log("Local proxy server not available:", e.message);
  }
  return false;
}

/**
 * Get proxy availability status
 */
export function isLocalProxyAvailable() {
  return localProxyAvailable;
}

// ============================================
// Configuration Storage
// ============================================

/**
 * Load saved configuration
 * Priority: User CRDT > localStorage (for migration)
 * @returns {Object} Configuration object
 */
export function loadConfig() {
  const defaultConfig = { calendars: [], corsProxy: "" };

  try {
    // Try User CRDT first (if user is logged in)
    if (UserCRDT.isEnabled()) {
      const crdtConfig = UserCRDT.getCalendarConfig();
      if (crdtConfig && (crdtConfig.calendars || crdtConfig.corsProxy)) {
        // CRDT has data - safe to clean up any backup from previous migration
        localStorage.removeItem(NC_STORAGE_KEY + "_backup");
        return crdtConfig;
      }

      // Check localStorage for migration
      const localConfig = localStorage.getItem(NC_STORAGE_KEY);
      if (localConfig) {
        const parsed = JSON.parse(localConfig);
        if (parsed.calendars && parsed.calendars.length > 0) {
          console.log("[Calendar] Migrating config from localStorage to User CRDT");
          try {
            UserCRDT.setCalendarConfig(parsed);
            // Only remove after successful migration
            // Keep a backup for one session in case of issues
            localStorage.setItem(NC_STORAGE_KEY + "_backup", localConfig);
            localStorage.removeItem(NC_STORAGE_KEY);
            console.log("[Calendar] Migration successful, backup saved");
          } catch (migrationErr) {
            console.error("[Calendar] Migration failed, keeping localStorage:", migrationErr);
            // Don't remove localStorage - keep using it
          }
          return parsed;
        }
      }

      // Check if we have a backup from a previous migration that we can restore
      const backupConfig = localStorage.getItem(NC_STORAGE_KEY + "_backup");
      if (backupConfig) {
        console.warn("[Calendar] CRDT empty but backup exists - restoring from backup");
        try {
          const parsed = JSON.parse(backupConfig);
          UserCRDT.setCalendarConfig(parsed);
          console.log("[Calendar] Restored from backup");
          return parsed;
        } catch (restoreErr) {
          console.error("[Calendar] Failed to restore from backup:", restoreErr);
        }
      }

      return defaultConfig;
    }

    // Fall back to localStorage (user not logged in)
    const saved = localStorage.getItem(NC_STORAGE_KEY);
    return saved ? JSON.parse(saved) : defaultConfig;
  } catch (e) {
    console.error("[Calendar] Error loading config:", e);
    return defaultConfig;
  }
}

/**
 * Save configuration
 * Uses User CRDT if logged in, otherwise localStorage
 * @param {Object} config - Configuration to save
 */
export function saveConfig(config) {
  try {
    if (UserCRDT.isEnabled()) {
      UserCRDT.setCalendarConfig(config);
      console.log("[Calendar] Config saved to User CRDT");
    } else {
      localStorage.setItem(NC_STORAGE_KEY, JSON.stringify(config));
      console.log("[Calendar] Config saved to localStorage");
    }
  } catch (e) {
    console.error("[Calendar] Error saving config:", e);
    // Fall back to localStorage
    localStorage.setItem(NC_STORAGE_KEY, JSON.stringify(config));
  }
}

/**
 * Generate unique calendar event ID.
 *
 * Uses intentional non-UUID format: "cal_" prefix creates namespace separation
 * from node IDs (which use UUIDs). Calendar events don't sync through CRDT,
 * so UUID collision avoidance isn't required. Timestamp prefix aids debugging.
 *
 * @returns {string} Calendar event ID
 */
function generateCalendarId() {
  return "cal_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
}

// ============================================
// ICS Parsing
// ============================================

/**
 * Parse iCalendar (ICS) data to extract events
 * @param {string} icsData - Raw ICS data
 * @param {string} calendarName - Name of the calendar
 * @param {string} calendarColor - Color for events
 * @returns {Array} Array of event objects
 */
export function parseICS(icsData, calendarName, calendarColor) {
  const events = [];
  const lines = icsData.split(/\r?\n/);
  let currentEvent = null;
  let currentKey = null;
  let currentValue = "";

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];

    // Handle line continuations (lines starting with space or tab)
    if (line.startsWith(" ") || line.startsWith("\t")) {
      currentValue += line.substring(1);
      continue;
    }

    // Process previous key-value if exists
    if (currentKey && currentEvent) {
      processICSProperty(currentEvent, currentKey, currentValue);
    }

    // Parse new line
    const colonIdx = line.indexOf(":");
    if (colonIdx === -1) continue;

    currentKey = line.substring(0, colonIdx);
    currentValue = line.substring(colonIdx + 1);

    if (line === "BEGIN:VEVENT") {
      currentEvent = { calendarName, calendarColor };
    } else if (line === "END:VEVENT" && currentEvent) {
      if (currentEvent.summary) {
        events.push(currentEvent);
      }
      currentEvent = null;
    }
  }

  return events;
}

function processICSProperty(event, key, value) {
  const baseKey = key.split(";")[0];
  const params = key.includes(";") ? key.substring(key.indexOf(";") + 1) : "";

  switch (baseKey) {
    case "SUMMARY":
      event.summary = unescapeICS(value);
      break;
    case "DTSTART":
      event.start = parseICSDate(value, params);
      event.allDay =
        params.includes("VALUE=DATE") && !params.includes("VALUE=DATE-TIME");
      break;
    case "DTEND":
      event.end = parseICSDate(value, params);
      break;
    case "LOCATION":
      event.location = unescapeICS(value);
      break;
    case "DESCRIPTION":
      event.description = unescapeICS(value);
      break;
  }
}

function unescapeICS(str) {
  return str
    .replace(/\\n/g, "\n")
    .replace(/\\,/g, ",")
    .replace(/\\;/g, ";")
    .replace(/\\\\/g, "\\");
}

function parseICSDate(dateStr, params) {
  if (dateStr.length === 8) {
    const year = parseInt(dateStr.substring(0, 4));
    const month = parseInt(dateStr.substring(4, 6)) - 1;
    const day = parseInt(dateStr.substring(6, 8));
    return new Date(year, month, day);
  }

  if (dateStr.includes("T")) {
    const year = parseInt(dateStr.substring(0, 4));
    const month = parseInt(dateStr.substring(4, 6)) - 1;
    const day = parseInt(dateStr.substring(6, 8));
    const hour = parseInt(dateStr.substring(9, 11));
    const minute = parseInt(dateStr.substring(11, 13));
    const second = parseInt(dateStr.substring(13, 15)) || 0;

    if (dateStr.endsWith("Z")) {
      return new Date(Date.UTC(year, month, day, hour, minute, second));
    }
    return new Date(year, month, day, hour, minute, second);
  }

  return new Date(dateStr);
}

// ============================================
// Event Fetching
// ============================================

/**
 * Fetch events from ICS URL (private link)
 * @param {Object} calendar - Calendar configuration
 * @param {string} corsProxy - CORS proxy URL
 * @returns {Promise<Array>} Array of events
 */
export async function fetchICSEvents(calendar, corsProxy) {
  let url = calendar.icsUrl.trim();

  // Convert webcal:// to https://
  if (url.startsWith("webcal://")) {
    url = url.replace("webcal://", "https://");
  }

  // Determine which proxy to use (local takes priority)
  if (localProxyAvailable && useLocalProxy) {
    url = LOCAL_PROXY_ENDPOINT + encodeURIComponent(url);
  } else if (corsProxy && corsProxy.trim()) {
    const proxy = corsProxy.trim();
    if (proxy.endsWith("?") || proxy.endsWith("=")) {
      url = proxy + encodeURIComponent(url);
    } else if (proxy.includes("?url=") || proxy.includes("&url=")) {
      url = proxy + encodeURIComponent(url);
    } else if (proxy.endsWith("/")) {
      url = proxy + url;
    } else {
      url = proxy + "/" + url;
    }
    console.log("Using external CORS proxy. Final URL:", url);
  } else {
    console.warn("No proxy available. Request may fail due to CORS.");
  }

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "text/calendar, text/plain, */*",
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => "");
      console.error("ICS fetch error response:", errorText.substring(0, 500));
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const icsData = await response.text();
    console.log("ICS data received, length:", icsData.length);

    if (!icsData.includes("BEGIN:VCALENDAR")) {
      console.error("Invalid ICS data:", icsData.substring(0, 500));
      throw new Error("Invalid calendar data received (not ICS format)");
    }

    return parseICS(icsData, calendar.name, calendar.color);
  } catch (error) {
    console.error("Fetch error for calendar", calendar.name, ":", error);

    // Detect CORS errors and provide helpful message
    if (
      error.message.includes("Failed to fetch") ||
      error.message.includes("NetworkError") ||
      error.name === "TypeError"
    ) {
      if (localProxyAvailable) {
        throw new Error(
          "Proxy request failed. Check if the domain is whitelisted in the server.",
        );
      } else if (!corsProxy || !corsProxy.trim()) {
        throw new Error(
          "CORS blocked. Run the local proxy server or add an external CORS proxy.",
        );
      } else {
        throw new Error(
          "CORS proxy failed. Try a different proxy or check the URL.",
        );
      }
    }
    throw error;
  }
}

/**
 * Fetch events from CalDAV (authenticated)
 * @param {Object} calendar - Calendar configuration
 * @param {string} corsProxy - CORS proxy URL (unused for CalDAV, uses local proxy)
 * @returns {Promise<Array>} Array of events
 */
export async function fetchCalDAVEvents(calendar, corsProxy) {
  const { caldavUrl, username, password, caldavCalendar, name, color } =
    calendar;

  const baseUrl = caldavUrl.replace(/\/$/, "");
  const calendarUrl = `${baseUrl}/remote.php/dav/calendars/${username}/${caldavCalendar}/`;

  const now = new Date();
  const start = new Date(now);
  start.setDate(start.getDate() - 1);
  start.setHours(0, 0, 0, 0);

  const end = new Date(now);
  end.setDate(end.getDate() + 8); // 7 days + buffer
  end.setHours(23, 59, 59, 999);

  const formatCalDAVDate = (d) => {
    return d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  };

  const reportBody = `<?xml version="1.0" encoding="UTF-8"?>
<c:calendar-query xmlns:d="DAV:" xmlns:c="urn:ietf:params:xml:ns:caldav">
    <d:prop>
        <d:getetag />
        <c:calendar-data />
    </d:prop>
    <c:filter>
        <c:comp-filter name="VCALENDAR">
            <c:comp-filter name="VEVENT">
                <c:time-range start="${formatCalDAVDate(start)}" end="${formatCalDAVDate(end)}"/>
            </c:comp-filter>
        </c:comp-filter>
    </c:filter>
</c:calendar-query>`;

  let xmlText;

  // Use local proxy if available
  if (localProxyAvailable && useLocalProxy) {
    console.log("Using local CalDAV proxy for:", calendarUrl);

    const proxyResponse = await fetch("/caldav-proxy", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        url: calendarUrl,
        method: "REPORT",
        headers: {
          "Content-Type": "application/xml; charset=utf-8",
          Depth: "1",
          Authorization: "Basic " + btoa(username + ":" + password),
        },
        body: reportBody,
      }),
    });

    if (!proxyResponse.ok) {
      const errorText = await proxyResponse.text();
      if (proxyResponse.status === 401) {
        throw new Error(
          "Authentication failed. Check username and app password.",
        );
      } else if (proxyResponse.status === 404) {
        throw new Error("Calendar not found. Check the calendar name.");
      }
      throw new Error(`CalDAV error: ${proxyResponse.status} - ${errorText}`);
    }

    xmlText = await proxyResponse.text();
  } else {
    // Direct request (will likely fail due to CORS)
    console.warn(
      "No local proxy available for CalDAV, attempting direct request...",
    );

    const headers = {
      "Content-Type": "application/xml; charset=utf-8",
      Depth: "1",
      Authorization: "Basic " + btoa(username + ":" + password),
    };

    const response = await fetch(calendarUrl, {
      method: "REPORT",
      headers,
      body: reportBody,
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Authentication failed");
      } else if (response.status === 404) {
        throw new Error("Calendar not found");
      }
      throw new Error(`Server error: ${response.status}`);
    }

    xmlText = await response.text();
  }

  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlText, "text/xml");
  const calDataElements = xmlDoc.getElementsByTagNameNS(
    "urn:ietf:params:xml:ns:caldav",
    "calendar-data",
  );

  const allEvents = [];
  for (const calData of calDataElements) {
    const icsContent = calData.textContent;
    if (icsContent) {
      const events = parseICS(icsContent, name, color);
      allEvents.push(...events);
    }
  }

  console.log(`CalDAV: Found ${allEvents.length} events from ${name}`);
  return allEvents;
}

/**
 * Fetch events from a single calendar
 * @param {Object} calendar - Calendar configuration
 * @param {string} corsProxy - CORS proxy URL
 * @returns {Promise<Array>} Array of events
 */
export async function fetchCalendarEvents(calendar, corsProxy) {
  if (calendar.type === "ics") {
    return await fetchICSEvents(calendar, corsProxy);
  } else if (calendar.type === "caldav") {
    return await fetchCalDAVEvents(calendar, corsProxy);
  }
  return [];
}

// ============================================
// Event Filtering & Formatting
// ============================================

/**
 * Filter events for the next N days
 * @param {Array} events - All events
 * @param {number} numDays - Number of days to include
 * @returns {Array} Array of day objects with events
 */
export function filterEventsForDays(events, numDays = 7) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  // Create array of days
  const days = [];
  for (let i = 0; i < numDays; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() + i);
    days.push({ date, events: [] });
  }

  // Assign events to their respective days
  events.forEach((event) => {
    if (!event.start) return;

    const eventStart = new Date(event.start);
    const eventDate = new Date(
      eventStart.getFullYear(),
      eventStart.getMonth(),
      eventStart.getDate(),
    );

    // Find matching day
    for (let i = 0; i < days.length; i++) {
      if (eventDate.getTime() === days[i].date.getTime()) {
        days[i].events.push(event);
        break;
      }
    }
  });

  // Sort events within each day
  const sortByTime = (a, b) => {
    if (a.allDay && !b.allDay) return -1;
    if (!a.allDay && b.allDay) return 1;
    return new Date(a.start) - new Date(b.start);
  };

  days.forEach((day) => {
    day.events.sort(sortByTime);
  });

  return days;
}

/**
 * Format time for display
 * @param {Date} date - Date object
 * @param {boolean} allDay - Whether it's an all-day event
 * @returns {string} Formatted time string
 */
function formatEventTime(date, allDay) {
  if (allDay) return t("app.calendar.allDay");
  return formatDate(date, { hour: "2-digit", minute: "2-digit" });
}

/**
 * Format date for day header
 * @param {Date} date - Date object
 * @returns {string} Formatted date string
 */
function formatDayDate(date) {
  return formatDate(date, { month: "short", day: "numeric" });
}

/**
 * Get day name
 * @param {Date} date - Date object
 * @param {number} index - Day index (0 = today)
 * @returns {string} Day name
 */
function getDayName(date, index) {
  if (index === 0) return t("common.today");
  if (index === 1) return t("common.tomorrow");
  return formatDate(date, { weekday: "short" });
}

// ============================================
// Dashboard Rendering
// ============================================

/**
 * Render calendar events in dashboard
 * @param {Array} days - Array of day objects
 * @param {Array} errors - Array of error objects
 */
function renderCalendarEvents(days, errors = []) {
  const contentEl = document.getElementById("nc-calendar-content");
  if (!contentEl) return;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const renderEventList = (events) => {
    if (events.length === 0) {
      return `<div class="dash-calendar-empty">${escapeHtml(t("app.calendar.noEvents"))}</div>`;
    }

    return `<div class="dash-calendar-event-list">
            ${events
              .map(
                (event) => `
                <div class="dash-calendar-event" style="border-left-color: ${event.calendarColor || "var(--accent-cyan)"}">
                    <div class="dash-calendar-event-time ${event.allDay ? "all-day" : ""}">
                        ${formatEventTime(new Date(event.start), event.allDay)}
                    </div>
                    <div class="dash-calendar-event-details">
                        <div class="dash-calendar-event-title">${_escapeHtml(event.summary)}</div>
                    </div>
                </div>
            `,
              )
              .join("")}
        </div>`;
  };

  let errorHtml = "";
  if (errors.length > 0) {
    errorHtml = `<div class="dash-calendar-error" style="margin-bottom: 12px; text-align: left; font-size: 11px;">
            <strong>${escapeHtml(t("app.calendar.failedToLoad"))}</strong> ${errors.map((e) => _escapeHtml(e.name)).join(", ")}
        </div>`;
  }

  // Only show days that have events, plus today
  const daysToShow = days.filter(
    (day, index) => index === 0 || day.events.length > 0,
  );

  contentEl.innerHTML = `
        ${errorHtml}
        <div class="dash-calendar-events">
            ${daysToShow
              .map((day) => {
                const isToday = day.date.getTime() === today.getTime();
                const originalIndex = days.indexOf(day);
                return `
                    <div class="dash-calendar-day ${isToday ? "is-today" : ""}">
                        <div class="dash-calendar-day-title">
                            <span class="day-name">${getDayName(day.date, originalIndex)}</span>
                            <span class="day-date">${formatDayDate(day.date)}</span>
                        </div>
                        ${renderEventList(day.events)}
                    </div>
                `;
              })
              .join("")}
        </div>
    `;
}

/**
 * Show loading state in calendar widget
 */
export function showLoading() {
  const contentEl = document.getElementById("nc-calendar-content");
  if (!contentEl) return;
  contentEl.innerHTML = `
        <div class="dash-calendar-loading">
            <div class="spinner"></div>
            <span>${escapeHtml(t("app.calendar.loading"))}</span>
        </div>
    `;
}

/**
 * Show setup state in calendar widget
 */
export function showSetup() {
  const contentEl = document.getElementById("nc-calendar-content");
  if (!contentEl) return;

  const proxyStatus = localProxyAvailable
    ? `<span style="color: var(--status-done);">${escapeHtml(t("app.calendar.proxyDetected"))}</span>`
    : `<span style="color: var(--text-muted);">${escapeHtml(t("app.calendar.runProxy"))}</span>`;

  contentEl.innerHTML = `
        <div class="dash-calendar-setup">
            <div class="dash-calendar-setup-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
            </div>
            <p>${escapeHtml(t("app.calendar.connectIntro"))}</p>
            <p style="font-size: 11px; margin-bottom: 16px;">${proxyStatus}</p>
            <button class="dash-calendar-setup-btn">${escapeHtml(t("app.calendar.addCalendars"))}</button>
        </div>
    `;

  const btn = contentEl.querySelector('.dash-calendar-setup-btn');
  if (btn) btn.addEventListener('click', () => window.OrbitalCalendar.openModal());
}

// ============================================
// Main Load Function
// ============================================

/**
 * Load and display all calendars
 */
export async function loadCalendar() {
  const config = loadConfig();
  console.log("Loaded calendar config:", JSON.stringify(config, null, 2));

  if (!config.calendars || config.calendars.length === 0) {
    showSetup();
    return;
  }

  // Determine proxy mode
  if (localProxyAvailable && !config.useExternalProxy) {
    useLocalProxy = true;
    console.log("Using local proxy server");
  } else {
    useLocalProxy = false;
    console.log("CORS Proxy configured:", config.corsProxy || "(none)");
  }

  showLoading();

  const allEvents = [];
  const errors = [];

  // Fetch events from all calendars in parallel
  const promises = config.calendars.map(async (calendar) => {
    try {
      const events = await fetchCalendarEvents(calendar, config.corsProxy);
      return { success: true, events };
    } catch (error) {
      console.error(`Error loading calendar ${calendar.name}:`, error);
      return { success: false, name: calendar.name, error: error.message };
    }
  });

  const results = await Promise.all(promises);

  results.forEach((result) => {
    if (result.success) {
      allEvents.push(...result.events);
    } else {
      errors.push({ name: result.name, error: result.error });
    }
  });

  const days = filterEventsForDays(allEvents, 7);
  renderCalendarEvents(days, errors);
}

// ============================================
// Modal Functions
// ============================================

/**
 * Create calendar item HTML for modal
 */
function createCalendarItemHTML(calendar, index) {
  const isICS = calendar.type === "ics";
  return `
        <div class="nc-calendar-item" data-index="${index}">
            <div class="nc-calendar-header">
                <input type="color" class="nc-calendar-color" value="${calendar.color}" data-field="color">
                <input type="text" class="nc-calendar-name-input" value="${_escapeHtml(calendar.name)}" placeholder="${escapeHtml(t("app.calendar.namePlaceholder"))}" data-field="name">
                <button class="nc-calendar-delete" title="${escapeHtml(t("app.calendar.remove"))}">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                </button>
            </div>

            <div class="nc-calendar-type-tabs">
                <button class="nc-calendar-type-tab ${isICS ? "active" : ""}" data-type="ics">${escapeHtml(t("app.calendar.typeIcs"))}</button>
                <button class="nc-calendar-type-tab ${!isICS ? "active" : ""}" data-type="caldav">${escapeHtml(t("app.calendar.typeCaldav"))}</button>
            </div>

            <div class="nc-calendar-fields ${isICS ? "active" : ""}" data-type="ics">
                <div class="nc-field">
                    <label>ICS URL</label>
                    <input type="url" placeholder="${escapeHtml(t("app.calendar.icsPlaceholder"))}" value="${_escapeHtml(calendar.icsUrl || "")}" data-field="icsUrl">
                    <div class="nc-field-hint">${escapeHtml(t("app.calendar.icsHint"))}</div>
                </div>
            </div>

            <div class="nc-calendar-fields ${!isICS ? "active" : ""}" data-type="caldav">
                <div class="nc-field">
                    <label>Nextcloud URL</label>
                    <input type="url" placeholder="https://your-nextcloud.com" value="${_escapeHtml(calendar.caldavUrl || "")}" data-field="caldavUrl">
                </div>
                <div class="nc-field-row">
                    <div class="nc-field">
                        <label>${escapeHtml(t("app.calendar.username"))}</label>
                        <input type="text" placeholder="${escapeHtml(t("app.calendar.usernamePlaceholder"))}" value="${_escapeHtml(calendar.username || "")}" data-field="username">
                    </div>
                    <div class="nc-field">
                        <label>${escapeHtml(t("app.calendar.appPassword"))}</label>
                        <input type="password" placeholder="********" value="${_escapeHtml(calendar.password || "")}" data-field="password">
                    </div>
                </div>
                <div class="nc-field">
                    <label>${escapeHtml(t("app.calendar.calendarName"))}</label>
                    <input type="text" placeholder="personal" value="${_escapeHtml(calendar.caldavCalendar || "personal")}" data-field="caldavCalendar">
                </div>
            </div>

            <div class="nc-calendar-status" data-status></div>
        </div>
    `;
}

/**
 * Render all calendar items in modal
 */
function renderCalendarItems() {
  const listEl = document.getElementById("nc-calendars-list");
  if (!listEl) return;

  listEl.innerHTML = ncModalCalendars
    .map((cal, i) => createCalendarItemHTML(cal, i))
    .join("");

  // Attach event listeners
  listEl.querySelectorAll(".nc-calendar-item").forEach((item) => {
    const index = parseInt(item.dataset.index);

    // Type tabs
    item.querySelectorAll(".nc-calendar-type-tab").forEach((tab) => {
      tab.addEventListener("click", () => {
        const type = tab.dataset.type;
        ncModalCalendars[index].type = type;
        item
          .querySelectorAll(".nc-calendar-type-tab")
          .forEach((tab) =>
            tab.classList.toggle("active", tab.dataset.type === type),
          );
        item
          .querySelectorAll(".nc-calendar-fields")
          .forEach((f) =>
            f.classList.toggle("active", f.dataset.type === type),
          );
      });
    });

    // Input fields
    item.querySelectorAll("input[data-field]").forEach((input) => {
      input.addEventListener("input", () => {
        ncModalCalendars[index][input.dataset.field] = input.value;
      });
    });

    // Delete button
    item.querySelector(".nc-calendar-delete").addEventListener("click", () => {
      ncModalCalendars.splice(index, 1);
      renderCalendarItems();
    });
  });
}

/**
 * Open calendar configuration modal
 */
export function openModal() {
  const modal = document.getElementById("nc-modal");
  if (!modal) return;

  const config = loadConfig();

  // Update proxy status section
  const proxyStatusContent = document.getElementById("nc-proxy-status-content");
  const proxyTitle = document.getElementById("nc-proxy-status-text");

  if (proxyStatusContent && proxyTitle) {
    if (localProxyAvailable) {
      proxyTitle.textContent = t("app.calendar.localProxyActive");
      proxyStatusContent.innerHTML = `
                <div style="display: flex; align-items: center; gap: 8px; color: var(--status-done); font-size: 13px; padding: 8px 0;">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    ${escapeHtml(t("app.calendar.localProxyPrivate"))}
                </div>
                <div class="nc-field" style="margin-top: 8px;">
                    <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
                        <input type="checkbox" id="nc-use-external-proxy" style="width: 16px; height: 16px;">
                        <span style="font-weight: normal; text-transform: none; letter-spacing: normal;">${escapeHtml(t("app.calendar.useExternal"))}</span>
                    </label>
                </div>
                <div id="nc-external-proxy-field" style="display: none; margin-top: 8px;">
                    <input type="url" id="nc-cors-proxy" placeholder="https://corsproxy.io/?" value="${config.corsProxy || ""}">
                    <div class="nc-field-hint">${escapeHtml(t("app.calendar.externalProxyUrl"))}</div>
                </div>
            `;

      // Add toggle handler
      setTimeout(() => {
        const checkbox = document.getElementById("nc-use-external-proxy");
        const externalField = document.getElementById(
          "nc-external-proxy-field",
        );
        if (checkbox && externalField) {
          checkbox.checked = config.useExternalProxy || false;
          externalField.style.display = checkbox.checked ? "block" : "none";
          checkbox.addEventListener("change", () => {
            externalField.style.display = checkbox.checked ? "block" : "none";
          });
        }
      }, 0);
    } else {
      proxyTitle.textContent = t("app.calendar.corsProxy");
      // The command goes in <code> inside the translated sentence.
      const tipHtml = escapeHtml(t("app.calendar.runCommand")).replace("{command}",
        () => '<code style="background: var(--bg-tertiary); padding: 2px 6px; border-radius: 4px;">python calendar_server.py</code>');
      proxyStatusContent.innerHTML = `
                <div style="font-size: 12px; color: var(--text-muted); margin-bottom: 8px;">
                    <strong>${escapeHtml(t("app.calendar.tip"))}</strong> ${tipHtml}
                </div>
                <div class="nc-field">
                    <input type="url" id="nc-cors-proxy" placeholder="https://corsproxy.io/?" value="${config.corsProxy || ""}">
                    <div class="nc-field-hint">${escapeHtml(t("app.calendar.proxyRequired"))}</div>
                </div>
            `;
    }
  }

  ncModalCalendars = JSON.parse(JSON.stringify(config.calendars || []));

  // Add a default calendar if none exist
  if (ncModalCalendars.length === 0) {
    ncModalCalendars.push({
      id: generateCalendarId(),
      name: t("app.calendar.defaultName"),
      color: NC_CALENDAR_COLORS[0],
      type: "ics",
      icsUrl: "",
      caldavUrl: "",
      username: "",
      password: "",
      caldavCalendar: "personal",
    });
  }

  renderCalendarItems();
  // Use unified modal system (handles focus trap, Escape key)
  showModalGeneric("nc-modal");
}

/**
 * Close calendar configuration modal
 */
export function closeModal() {
  closeModalGeneric("nc-modal");
}

/**
 * Add new calendar to modal
 */
export function addCalendar() {
  const colorIndex = ncModalCalendars.length % NC_CALENDAR_COLORS.length;
  ncModalCalendars.push({
    id: generateCalendarId(),
    name: t("app.calendar.numberedName", { n: ncModalCalendars.length + 1 }),
    color: NC_CALENDAR_COLORS[colorIndex],
    type: "ics",
    icsUrl: "",
    caldavUrl: "",
    username: "",
    password: "",
    caldavCalendar: "personal",
  });
  renderCalendarItems();

  // Scroll to new calendar
  const listEl = document.getElementById("nc-calendars-list");
  if (listEl) listEl.scrollTop = listEl.scrollHeight;
}

/**
 * Save calendar settings from modal
 */
export function saveSettings() {
  // Validate calendars
  const validCalendars = ncModalCalendars.filter((cal) => {
    if (cal.type === "ics") {
      return cal.icsUrl && cal.icsUrl.trim();
    } else {
      return cal.caldavUrl && cal.username && cal.password;
    }
  });

  // Get external proxy settings
  const corsProxyInput = document.getElementById("nc-cors-proxy");
  const useExternalCheckbox = document.getElementById("nc-use-external-proxy");

  const config = {
    corsProxy: corsProxyInput ? corsProxyInput.value.trim() : "",
    useExternalProxy: useExternalCheckbox ? useExternalCheckbox.checked : false,
    calendars: validCalendars,
  };

  console.log("Saving calendar config:", JSON.stringify(config, null, 2));

  // Update proxy flag
  if (localProxyAvailable && !config.useExternalProxy) {
    useLocalProxy = true;
  } else {
    useLocalProxy = false;
  }

  saveConfig(config);
  closeModal();
  loadCalendar();
}

/**
 * Setup event listeners for calendar UI
 */
export function setupEventListeners() {
  document
    .getElementById("nc-config-btn")
    ?.addEventListener("click", openModal);
  document.getElementById("nc-setup-btn")?.addEventListener("click", openModal);
  document.getElementById("nc-cancel")?.addEventListener("click", closeModal);
  document
    .getElementById("nc-add-calendar")
    ?.addEventListener("click", addCalendar);
  document.getElementById("nc-save")?.addEventListener("click", saveSettings);
  document.getElementById("nc-modal")?.addEventListener("click", (e) => {
    if (e.target.id === "nc-modal") closeModal();
  });

  // Keyboard handling is provided by showModalGeneric's focus trap
}

// ============================================
// Exports for global access
// ============================================

export const CALENDAR_COLORS = NC_CALENDAR_COLORS;
